import { useCallback, useContext } from 'react';
import { breakDownSeconds, formatNum } from '../../utils/timeUtils';
import { SettingsContext } from '../../providers/SettingsProvider';

function formatDateDefault(timestamp: number) {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
}

function formatDateISO(timestamp: number) {
    const date = new Date(timestamp * 1000);
    return `${date.getFullYear()}-${formatNum(date.getMonth() + 1)}-${formatNum(date.getDay())}`;
}

function formatDateShort(timestamp: number) {
    const date = new Date(timestamp * 1000);
    return `${formatNum(date.getMonth() + 1)}/${formatNum(date.getDay())}/${date.getFullYear()}`;
}

function formatDateLong(timestamp: number) {
    const date = new Date(timestamp * 1000);
    return `${date.toLocaleDateString(undefined, { month: 'short' })} ${formatNum(date.getDay() + 1)}, ${date.getFullYear()}`;
}

function formatTime24Hour(timestamp: number, includeSeconds?: boolean) {
    const date = new Date(timestamp * 1000);

    const time = `${formatNum(date.getHours())}:${formatNum(date.getMinutes())}`;
    return includeSeconds
        ? `${time}:${formatNum(date.getSeconds())}`
        : time;
}

function formatTime12Hour(timestamp: number, includeSeconds?: boolean) {
    const date = new Date(timestamp * 1000);

    let hours = date.getHours();
    let amPm = 'AM';

    if (hours === 0) {
        hours = 12;
    }
    else if (hours > 12) {
        hours -= 12;
        amPm = 'PM';
    }

    let time = `${hours}:${formatNum(date.getMinutes())}`;

    if (includeSeconds)
        time = `${time}:${formatNum(date.getSeconds())}`;

    return `${time} ${amPm}`;
}

function formatTimeSpanNormal(timeSeconds: number): string {
    const { days, hours, minutes, seconds } = breakDownSeconds(timeSeconds);
    let result = `${formatNum(minutes)}:${formatNum(seconds)}`;

    if (hours > 0)
        result = `${formatNum(hours)}:${result}`;

    if (days > 0)
        result = `${days}\xa0day${days > 1 ? 's' : ''} ${result}`;

    return result;
}

function formatTimeSpanWritten(timeSeconds: number): string {
    const { days, hours, minutes, seconds } = breakDownSeconds(timeSeconds);
    let result = '';

    if (timeSeconds === 0 || seconds !== 0)
        result = `${seconds}s`;

    if (minutes > 0)
        result = `${minutes}m${result ? '\xa0' : ''}${result}`; // nbsp

    if (hours > 0)
        result = `${hours}h${result ? '\x20' : ''}${result}`; // regular (breaking) space

    if (days > 0)
        result = `${days}d${result ? '\x20' : ''}${result}`; // regular (breaking) space

    return result;
}

interface HookReturnType {
    formatDate(timestamp: number): string;
    formatTime(timestamp: number, includeSeconds?: boolean): string;
    formatDateTime(timestamp: number, includeSeconds?: boolean): string;
    formatTimeSpan(seconds: number): string;
}

function useTimeFormatters(): HookReturnType {
    const { settings: { dateFormat, timeFormat, timeSpanFormat } } = useContext(SettingsContext);

    const formatDate = useCallback(
        (timestamp: number) => {
            switch (dateFormat) {
                case 'default':
                    return formatDateDefault(timestamp);
                case 'iso':
                    return formatDateISO(timestamp);
                case 'short':
                    return formatDateShort(timestamp);
                case 'long':
                    return formatDateLong(timestamp);
            }
            throw new Error(`Invalid time format '${timeFormat}'`);
        },
        [dateFormat, timeFormat],
    );

    const formatTime = useCallback(
        (timestamp: number, includeSeconds?: boolean) => {
            switch (timeFormat) {
                case '24hour':
                    return formatTime24Hour(timestamp, includeSeconds);
                case '12hour':
                    return formatTime12Hour(timestamp, includeSeconds);
            }
            throw new Error(`Invalid time format '${timeFormat}'`);
        },
        [timeFormat],
    );

    const formatDateTime = useCallback(
        (timestamp: number, includeSeconds?: boolean) => `${formatDate(timestamp)}\u2002${formatTime(timestamp, includeSeconds)}`,
        [formatDate, formatTime],
    );

    const formatTimeSpan = useCallback(
        (seconds: number) => {
            switch (timeSpanFormat) {
                case 'normal':
                    return formatTimeSpanNormal(seconds);
                case 'written':
                    return formatTimeSpanWritten(seconds);
            }
            throw new Error(`Invalid time span format '${timeSpanFormat}'`);
        },
        [timeSpanFormat],
    );

    return {
        formatDate,
        formatTime,
        formatDateTime,
        formatTimeSpan,
    };
}

export default useTimeFormatters;